import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/privacy.scss";
import "../css/delivery.scss";

const updatedDate = "2026-08-14";
const updatedDateLabel = "14 August 2026";

function Delivery() {
  const shippingRows = [
    [
      "Standard tracked",
      "3–10 business days after dispatch",
      `${siteConfig.commerce.currencySymbol}${siteConfig.commerce.standardShipping}`,
    ],
    [
      "Orders over threshold",
      "3–10 business days after dispatch",
      `Free from ${siteConfig.commerce.currencySymbol}${siteConfig.commerce.shippingThreshold}`,
    ],
    [
      "Pre-order pieces",
      "Product dispatch estimate + standard transit",
      "Shown at checkout",
    ],
  ];

  const journey = [
    [
      "Order received",
      "We email your order reference after checkout. This confirms receipt of the request.",
    ],
    [
      "Being prepared",
      "Available pieces are checked and packed. Pre-order dispatch timing appears on the product page.",
    ],
    [
      "On the way",
      "When the carrier accepts the parcel, we send tracking to the email used at checkout.",
    ],
    [
      "Delivered",
      "Follow the carrier link for the latest scan and check the nominated safe place where applicable.",
    ],
  ];

  const sections = [
    {
      id: "options",
      title: "Options, timing & cost",
      content: (
        <>
          <p>
            Delivery methods are based on destination, parcel size and
            availability. The checkout total is the final quote for your
            address. Estimates begin when the parcel is dispatched, not when the
            order is placed.
          </p>
          <div
            className="policy-table-wrap"
            role="region"
            aria-label="Shipping options"
            tabIndex={0}
          >
            <table className="policy-table">
              <thead>
                <tr>
                  <th scope="col">Service</th>
                  <th scope="col">Estimated transit</th>
                  <th scope="col">Cost</th>
                </tr>
              </thead>
              <tbody>
                {shippingRows.map(([service, timing, cost]) => (
                  <tr key={service}>
                    <th scope="row">{service}</th>
                    <td>{timing}</td>
                    <td>{cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <aside className="policy-note" aria-label="Delivery estimate note">
            <span className="eyebrow">Worth knowing</span>
            <p>
              Business days exclude weekends and public holidays. Carrier
              estimates are not guaranteed appointments.
            </p>
          </aside>
        </>
      ),
    },
    {
      id: "destinations",
      title: "Destinations & addresses",
      content: (
        <>
          <p>
            We deliver to the countries and address types offered at checkout.
            Some carriers cannot serve PO boxes, military addresses or remote
            postcodes. If an address is unavailable, the checkout will ask for
            an alternative.
          </p>
          <p>
            Review the name, postcode, unit number and phone before submitting
            your order. Contact us immediately if something is wrong. Once
            packing or carrier processing begins, an address change may no
            longer be possible.
          </p>
        </>
      ),
    },
    {
      id: "journey",
      title: "From studio to doorstep",
      content: (
        <ol className="policy-steps">
          {journey.map(([title, copy], index) => (
            <li key={title}>
              <span>{String(index + 1).padStart(2, "0")}</span>
              <div>
                <h3>{title}</h3>
                <p>{copy}</p>
              </div>
            </li>
          ))}
        </ol>
      ),
    },
    {
      id: "preorders",
      title: "Pre-orders & split parcels",
      content: (
        <>
          <p>
            Pre-order pieces are produced in considered quantities and carry an
            estimated dispatch date. Add the standard transit estimate for your
            destination to understand the likely arrival window.
          </p>
          <p>
            An order containing available and pre-order items may wait until
            every piece is ready or arrive in more than one parcel. We will show
            separate tracking when split delivery is used, without adding an
            unapproved fee.
          </p>
        </>
      ),
    },
    {
      id: "customs",
      title: "Customs, tax & import charges",
      content: (
        <>
          <p>
            Tax and duty treatment depends on destination and is shown at
            checkout where it can be calculated. When charges are not collected
            in advance, the local authority or carrier may request payment
            before delivery.
          </p>
          <p>
            The recipient is responsible for lawful import charges unless
            checkout expressly states that duties are included. Refusing a
            parcel may create carrier, customs and return costs that can be
            deducted where permitted by law.
          </p>
        </>
      ),
    },
    {
      id: "problems",
      title: "Late, missing or damaged parcels",
      content: (
        <>
          <ul className="policy-list policy-list--divided">
            <li>
              <strong>Running late</strong>
              <span>
                Check tracking first; if scans stop beyond the estimate, send us
                the order number.
              </span>
            </li>
            <li>
              <strong>Marked delivered</strong>
              <span>
                Check the address, safe place, household and neighbours, then
                contact us promptly.
              </span>
            </li>
            <li>
              <strong>Arrived damaged</strong>
              <span>
                Keep the item, mailer and label, and photograph them before
                disposal.
              </span>
            </li>
            <li>
              <strong>Returned to sender</strong>
              <span>
                We will review the carrier reason and explain redelivery or
                refund options.
              </span>
            </li>
          </ul>
          <p>
            Carrier investigations take time, but our team will keep the request
            connected to your order until there is an outcome.
          </p>
        </>
      ),
    },
    {
      id: "packaging",
      title: "Considered packaging",
      content: (
        <>
          <p>
            We size packaging to the order and favour recyclable or
            recycled-content materials where suitable. A compact parcel protects
            the garment while avoiding unnecessary volume in transit. Please
            reuse the mailer for an authorised return when it remains secure, or
            recycle it according to local guidance.
          </p>
        </>
      ),
    },
    {
      id: "support",
      title: "Delivery support",
      content: (
        <>
          <p>
            Include your order number and delivery postcode so we can find the
            parcel quickly. Our team replies during {siteConfig.contact.hours}.
          </p>
          <address className="policy-address">
            <strong>{siteConfig.brandName} delivery team</strong>
            <a href={`mailto:${siteConfig.contact.email}`}>
              {siteConfig.contact.email}
            </a>

            <span>{siteConfig.contact.address}</span>
          </address>
        </>
      ),
    },
  ];

  return (
    <main
      className="delivery policy-page policy-page--delivery"
      aria-labelledby="delivery-title"
    >
      <header className="policy-hero page-shell">
        <div className="policy-hero__label">
          <p className="eyebrow">Customer care · 04</p>
          <span>From our coast to your door.</span>
        </div>
        <div className="policy-hero__copy">
          <h1 className="page-heading" id="delivery-title">
            Shipping,
            <br />
            <em>charted.</em>
          </h1>
          <p>
            Tracked standard delivery, free from{" "}
            {siteConfig.commerce.currencySymbol}
            {siteConfig.commerce.shippingThreshold}, with each stage in view.
          </p>
        </div>
        <div className="policy-hero__date">
          <span className="eyebrow">Guide updated</span>
          <time dateTime={updatedDate}>{updatedDateLabel}</time>
        </div>
      </header>

      <div className="policy-layout page-shell">
        <aside className="policy-toc">
          <div className="policy-toc__inner">
            <p className="eyebrow">Shipping guide</p>
            <nav aria-label="Shipping guide sections">
              <ol>
                {sections.map((section, index) => (
                  <li key={section.id}>
                    <a href={`#${section.id}`}>
                      <span>{String(index + 1).padStart(2, "0")}</span>
                      {section.title}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
            <div className="policy-toc__help">
              <span className="eyebrow">Track down help</span>
              <Link to="/contact">
                Contact customer care <span aria-hidden="true">↗</span>
              </Link>
            </div>
          </div>
        </aside>

        <article className="policy-content">
          {sections.map((section, index) => (
            <section
              className="policy-section"
              id={section.id}
              aria-labelledby={`${section.id}-title`}
              key={section.id}
            >
              <span className="policy-section__number" aria-hidden="true">
                {String(index + 1).padStart(2, "0")}
              </span>
              <div className="policy-section__body">
                <h2 id={`${section.id}-title`}>{section.title}</h2>
                {section.content}
              </div>
            </section>
          ))}
        </article>
      </div>

      <section
        className="policy-next page-shell"
        aria-labelledby="delivery-next-title"
      >
        <div>
          <p className="eyebrow">Destination: everyday</p>
          <h2 id="delivery-next-title">
            Considered pieces, carefully on their way.
          </h2>
        </div>
        <div className="policy-next__links">
          <Link className="button-light" to="/shop">
            Shop the collection <span aria-hidden="true">→</span>
          </Link>
          <Link className="policy-next__text-link" to="/returns">
            Review returns <span aria-hidden="true">↗</span>
          </Link>
        </div>
      </section>
    </main>
  );
}

export default Delivery;
